import React, { useState, useEffect, useCallback } from 'react';
import { getToken as getAppCheckToken } from 'firebase/app-check';
import { getIdToken, onAuthStateChanged } from 'firebase/auth';
import { appCheck, auth } from '../../../firebase';
import './Timestamp.css';
import StampPop from './StampPop';
import VideoList from './VideoList';

const HISTORY_STORAGE_KEY = 'timestamp_history';
const MAX_HISTORY_ITEMS = 20;
const MAX_VIDEO_DURATION_MINUTES = 30;
const LONG_VIDEO_MESSAGE =
  'Heads-up: this epic runs longer than 30 minutes—our free bumpups crew clocks out at the half-hour mark. For longer videos paid version is required. Contact us and we will roll out the red carpet.';

const youtubeUrlPattern = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})([&?].*)?$/i;

const parseYouTubeDuration = (isoDuration) => {
  if (!isoDuration || typeof isoDuration !== 'string') {
    return null;
  }

  const match = isoDuration.match(
    /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/
  );

  if (!match) {
    return null;
  }

  const [, hours = '0', minutes = '0', seconds = '0'] = match;
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
};

const formatMinutes = (totalSeconds) => {
  if (typeof totalSeconds !== 'number' || Number.isNaN(totalSeconds)) {
    return null;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) {
    return `${seconds}s`;
  }
  if (seconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${seconds}s`;
};

const toHistoryKey = (value) => {
  if (!value) {
    return '';
  }

  const match = value.trim().match(youtubeUrlPattern);
  if (match) {
    return match[4];
  }

  return value.trim();
};

const attachHistoryKey = (entry) => {
  if (!entry || entry.historyKey) {
    return entry;
  }

  const historyKey = toHistoryKey(entry.url);
  if (!historyKey) {
    return entry;
  }

  return { ...entry, historyKey };
};

const getEntryKey = (entry) => (entry ? entry.historyKey || toHistoryKey(entry.url) : '');

const loadHistoryFromStorage = () => readHistoryFromStorage().map(attachHistoryKey);

const readHistoryFromStorage = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to read timestamp history from storage:', error);
    return [];
  }
};

const writeHistoryToStorage = (items) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Failed to persist timestamp history to storage:', error);
  }
};

const waitForAuthUser = () => {
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve, reject) => {
    let completed = false;
    let unsubscribe = () => {};
    let timeoutId;

    const finish = (callback) => (value) => {
      if (!completed) {
        completed = true;
        clearTimeout(timeoutId);
        unsubscribe();
        callback(value);
      }
    };

    timeoutId = setTimeout(() => {
      finish(reject)(new Error('Authentication timed out.'));
    }, 10000);

    unsubscribe = onAuthStateChanged(
      auth,
      finish((user) => {
        if (user) {
          resolve(user);
        } else {
          reject(new Error('User not signed in.'));
        }
      }),
      finish((error) => {
        reject(error);
      })
    );
  });
};

const buildSecurityHeaders = async () => {
  const [appCheckResult, user] = await Promise.all([
    getAppCheckToken(appCheck, false),
    waitForAuthUser(),
  ]);

  if (!appCheckResult || !appCheckResult.token) {
    throw new Error('Unable to acquire App Check token.');
  }

  const idToken = await getIdToken(user);
  if (!idToken) {
    throw new Error('Unable to acquire auth token.');
  }

  return {
    Authorization: `Bearer ${idToken}`,
    'X-Firebase-AppCheck': appCheckResult.token,
  };
};

const Timestamp = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTracking, setIsTracking] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [videoData, setVideoData] = useState(null);
  const [isFetchingVideo, setIsFetchingVideo] = useState(false);
  const [isGeneratingTimestamps, setIsGeneratingTimestamps] = useState(false);
  const [timestampStatus, setTimestampStatus] = useState(null);
  const [timestampResult, setTimestampResult] = useState(null);
  const [isStampPopOpen, setIsStampPopOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState(() => loadHistoryFromStorage());
  const [isRefreshingHistory, setIsRefreshingHistory] = useState(false);

  const exampleUrl = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ';

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      if (isTracking && startTime) {
        setElapsedTime(Date.now() - startTime);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isTracking, startTime]);

  const handleStartStop = () => {
    if (isTracking) {
      setIsTracking(false);
      setStartTime(null);
    } else {
      setIsTracking(true);
      setStartTime(Date.now());
      setElapsedTime(0);
    }
  };

  const formatTime = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return `${hours.toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const resetVideoForm = useCallback(() => {
    setVideoUrl('');
    setVideoData(null);
    setUrlError('');
  }, []);

  const handleUrlChange = (event) => {
    setVideoUrl(event.target.value);
    if (urlError) {
      setUrlError('');
    }
    if (videoData) {
      setVideoData(null);
    }
    if (timestampStatus) {
      setTimestampStatus(null);
    }
    if (timestampResult) {
      setTimestampResult(null);
    }
    if (isStampPopOpen) {
      setIsStampPopOpen(false);
    }
  };

  const handleGenerate = async (event) => {
    event.preventDefault();
    const trimmedUrl = videoUrl.trim();

    if (!trimmedUrl) {
      setUrlError(`Please enter a YouTube URL (e.g., ${exampleUrl}).`);
      setVideoData(null);
      return;
    }

    const match = trimmedUrl.match(youtubeUrlPattern);
    if (!match) {
      setUrlError(`Please enter a valid YouTube URL (e.g., ${exampleUrl}).`);
      setVideoData(null);
      return;
    }

    const requestedKey = toHistoryKey(trimmedUrl);
    const existingEntry = historyItems.find((item) => getEntryKey(item) === requestedKey);

    if (existingEntry) {
      setTimestampStatus(null);
      setTimestampResult(existingEntry);
      setIsStampPopOpen(true);
      resetVideoForm();
      return;
    }

    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;

    if (!apiKey) {
      setUrlError('Missing YouTube API key. Please set REACT_APP_YOUTUBE_API_KEY in your environment.');
      setVideoData(null);
      return;
    }

    const videoId = match[4];

    try {
      setIsFetchingVideo(true);
      setUrlError('');
      setVideoData(null);
      if (timestampStatus) {
        setTimestampStatus(null);
      }
      if (timestampResult) {
        setTimestampResult(null);
      }
      if (isStampPopOpen) {
        setIsStampPopOpen(false);
      }

      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=${apiKey}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Unable to fetch video details.');
      }

      const item = data.items?.[0];

      if (!item) {
        throw new Error('Video not found. Please check the URL.');
      }

      const { title, thumbnails } = item.snippet;
      const durationSeconds = parseYouTubeDuration(item.contentDetails?.duration);
      const durationLabel = formatMinutes(durationSeconds);
      const exceedsFreeLimit =
        typeof durationSeconds === 'number' &&
        durationSeconds / 60 > MAX_VIDEO_DURATION_MINUTES;
      const thumbnailUrl =
        thumbnails?.maxres?.url ||
        thumbnails?.standard?.url ||
        thumbnails?.high?.url ||
        thumbnails?.medium?.url ||
        thumbnails?.default?.url;

      setVideoData({
        title,
        thumbnailUrl,
        durationSeconds,
        durationLabel,
        exceedsFreeLimit,
      });
    } catch (error) {
      console.error(error);
      setUrlError(error.message || 'Unable to fetch video details.');
      setVideoData(null);
    } finally {
      setIsFetchingVideo(false);
    }
  };

  const refreshHistoryFromStorage = useCallback(() => {
    const latest = loadHistoryFromStorage();
    setHistoryItems(latest);
  }, []);

  const getTimestampFunctionUrl = () => {
    const projectId =
      process.env.REACT_APP_PROJECT_ID || process.env.REACT_APP_FIREBASE_PROJECT_ID;

    if (!projectId) {
      return null;
    }

    const region = process.env.REACT_APP_FUNCTION_REGION || 'us-central1';
    const trimmed = (value) => (value ? value.replace(/\/$/, '') : value);

    const explicitOrigin = trimmed(process.env.REACT_APP_FUNCTIONS_ORIGIN);
    if (explicitOrigin) {
      return `${explicitOrigin}/${region}/generate_timestamps`;
    }

    const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const useEmulator =
      process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true' && isLocalHost;

    if (useEmulator) {
      const emulatorHost = trimmed(
        process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST || 'http://localhost:5001'
      );
      return `${emulatorHost}/${projectId}/${region}/generate_timestamps`;
    }

    return `https://${region}-${projectId}.cloudfunctions.net/generate_timestamps`;
  };

  const handleGenerateTimestamps = async () => {
    const trimmedUrl = videoUrl.trim();

    if (!trimmedUrl) {
      setTimestampStatus({
        type: 'error',
        message: 'Missing video URL. Please enter a valid YouTube link first.',
      });
      return;
    }

    const requestedKey = toHistoryKey(trimmedUrl);
    const existingEntry = historyItems.find((item) => getEntryKey(item) === requestedKey);

    if (existingEntry) {
      setTimestampStatus(null);
      setTimestampResult(existingEntry);
      setIsStampPopOpen(true);
      resetVideoForm();
      return;
    }

    if (videoData?.exceedsFreeLimit) {
      setTimestampStatus({ type: 'info', message: LONG_VIDEO_MESSAGE });
      setIsStampPopOpen(false);
      return;
    }

    const functionUrl = getTimestampFunctionUrl();

    if (!functionUrl) {
      setTimestampStatus({
        type: 'error',
        message: 'Missing Firebase project configuration. Set REACT_APP_PROJECT_ID in your environment.',
      });
      return;
    }

    console.log('[Timestamp] prepare-request', {
      functionUrl,
      payload: { url: trimmedUrl },
    });

    try {
      setIsGeneratingTimestamps(true);
      setTimestampStatus(null);

      let securityHeaders;
      try {
        securityHeaders = await buildSecurityHeaders();
      } catch (tokenError) {
        console.error('Failed to prepare Firebase security headers:', tokenError);
        setTimestampStatus({
          type: 'error',
          message:
            'Unable to verify Firebase session. Please refresh the page and try again.',
        });
        return;
      }

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...securityHeaders,
        },
        body: JSON.stringify({ url: trimmedUrl }),
      });

      const responseText = await response.text();
      let data = null;

      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('Unable to parse timestamp response as JSON:', parseError);
        }
      }

      if (!response.ok) {
        const message = (data && data.error) || `Failed to generate timestamps (status ${response.status}).`;
        throw new Error(message);
      }

      if (!data || !Array.isArray(data.timestamps_list) || data.timestamps_list.length === 0) {
        throw new Error('Timestamp service returned no data.');
      }

      setTimestampStatus(null);
      const newEntry = {
        id: `ts-${Date.now()}`,
        url: data.url || trimmedUrl,
        model: data.model || null,
        language: data.language || null,
        timestampsStyle: data.timestamps_style || null,
        timestampsList: data.timestamps_list,
        timestampsString: data.timestamps_string || data.timestamps_list.join('\n'),
        videoDuration: data.video_duration || null,
        title: videoData?.title || 'Untitled video',
        thumbnail: videoData?.thumbnailUrl || null,
        generatedAt: new Date().toISOString(),
        historyKey: requestedKey,
      };

      const existingHistory = loadHistoryFromStorage();
      const filteredHistory = existingHistory.filter((item) => getEntryKey(item) !== requestedKey);
      const updatedHistory = [newEntry, ...filteredHistory].slice(0, MAX_HISTORY_ITEMS);
      const finalHistory = updatedHistory.map(attachHistoryKey);

      writeHistoryToStorage(finalHistory);
      setHistoryItems(finalHistory);

      setTimestampResult(newEntry);
      setIsStampPopOpen(true);
      resetVideoForm();
    } catch (error) {
      console.error('Failed to generate timestamps:', error);
      setTimestampStatus({
        type: 'error',
        message: error.message || 'Unable to generate timestamps.',
      });
      setTimestampResult(null);
      setIsStampPopOpen(false);
    } finally {
      setIsGeneratingTimestamps(false);
    }
  };

  const handleHistoryRefresh = () => {
    if (isRefreshingHistory) {
      return;
    }

    setIsRefreshingHistory(true);
    setTimeout(() => {
      refreshHistoryFromStorage();
      setIsRefreshingHistory(false);
    }, 200);
  };

  const handleHistorySelect = (item) => {
    if (!item) {
      return;
    }

    setTimestampStatus(null);
    setTimestampResult(item);
    setIsStampPopOpen(true);
  };

  const handleHistoryDelete = useCallback((item) => {
    if (!item) {
      return;
    }

    const targetKey = getEntryKey(item);
    let removed = false;

    setHistoryItems((previous) => {
      const next = previous.filter((entry) => getEntryKey(entry) !== targetKey);
      removed = next.length !== previous.length;
      const normalizedNext = next.map(attachHistoryKey);
      writeHistoryToStorage(normalizedNext);
      return normalizedNext;
    });

    if (removed) {
      setTimestampResult((prev) => {
        if (prev && getEntryKey(prev) === targetKey) {
          setIsStampPopOpen(false);
          return null;
        }
        return prev;
      });
      setTimestampStatus({ type: 'info', message: 'History entry removed.' });
    }
  }, []);

  const isUrlInvalid = Boolean(urlError);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorageChange = (event) => {
      if (event.key === HISTORY_STORAGE_KEY) {
        refreshHistoryFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refreshHistoryFromStorage]);

  return (
    <section className="timestamp-section" id="use-cases">
      <div className="timestamp-container">
        <div className="timestamp-headline">
          <span className="section-tag subtle">Stamp Sage Timestamp</span>
          <h2 className="timestamp-title">One workspace to timestamp, track, and ship every upload</h2>
          <p className="timestamp-intro">
            Paste any YouTube link and Timestamp spins up AI-generated chapters, keeps past runs within reach, and tracks the production minutes that make each release possible.
          </p>
        </div>

        <div
          className="timestamp-tool-card"
          id="timestamp"
          role="region"
          aria-labelledby="timestamp-tool-heading"
        >
          <div className="timestamp-tool-card-head">
            <h3 id="timestamp-tool-heading">AI Timestamp Generator</h3>
            <p>
              Drop in a YouTube URL and let Stamp Sage’s bump-1.0 model craft clean chapters, highlight moments, and publish-ready copy.
              Preview video metadata instantly, save every run to history, and revisit quickstart tutorials whenever you need a refresher{' '}
              <a href="#tutorials" className="timestamp-tool-link">here</a>.
            </p>
          </div>

          <form
            className="hero-tool-form"
            aria-label="Generate timestamps"
            onSubmit={handleGenerate}
            noValidate
          >
            <label className="sr-only" htmlFor="hero-video-url">YouTube video URL</label>
            <div className="hero-tool-input-group">
              <input
                type="url"
                id="hero-video-url"
                name="videoUrl"
                placeholder="Enter YouTube video URL"
                autoComplete="url"
                value={videoUrl}
                onChange={handleUrlChange}
                aria-invalid={isUrlInvalid}
                aria-describedby={isUrlInvalid ? 'hero-video-url-error' : undefined}
              />
              <button type="submit" className="hero-tool-submit">Generate</button>
            </div>
            {urlError && (
              <p className="hero-tool-error" id="hero-video-url-error" role="alert">{urlError}</p>
            )}
          </form>
          {timestampStatus && !isFetchingVideo && !videoData && (
            <p
              className={`hero-tool-feedback ${timestampStatus.type === 'error' ? 'error' : timestampStatus.type === 'info' ? 'info' : 'success'}`}
              role="status"
            >
              {timestampStatus.message}
            </p>
          )}
          {isFetchingVideo && (
            <div className="video-preview loading" role="status">Loading video details...</div>
          )}
          {!isFetchingVideo && videoData && (
            <div className="video-preview" aria-live="polite">
              {videoData.thumbnailUrl && (
                <img src={videoData.thumbnailUrl} alt={`Thumbnail for ${videoData.title}`} />
              )}
              <p className="video-title">{videoData.title}</p>
              {videoData.durationLabel && (
                <p className="video-duration">Duration: {videoData.durationLabel}</p>
              )}
              {videoData.exceedsFreeLimit && (
                <p className="hero-tool-feedback info" role="status">{LONG_VIDEO_MESSAGE}</p>
              )}
              <div className="generate-timestamps-actions">
                <button
                  type="button"
                  className="hero-tool-submit secondary"
                  onClick={handleGenerateTimestamps}
                  disabled={isGeneratingTimestamps || videoData.exceedsFreeLimit}
                >
                  {isGeneratingTimestamps ? (
                    <span className="loading-label">
                      Generating
                      <span className="loading-dots" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </span>
                    </span>
                  ) : (
                    'Generate Timestamps'
                  )}
                </button>
                {timestampStatus && (
                  <p
                    className={`hero-tool-feedback ${timestampStatus.type === 'error' ? 'error' : timestampStatus.type === 'info' ? 'info' : 'success'}`}
                    role="status"
                  >
                    {timestampStatus.message}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <VideoList
          items={historyItems}
          onRefresh={handleHistoryRefresh}
          onSelect={handleHistorySelect}
          onDelete={handleHistoryDelete}
          refreshing={isRefreshingHistory}
          layout="embedded"
        />

        <section className="time-tracker-card" aria-labelledby="time-tracker-heading">
          <div className="time-tracker-header">
            <span className="section-tag subtle">Session Timer</span>
            <h3 className="time-tracker-title" id="time-tracker-heading">FlowTrack mini</h3>
            <p className="time-tracker-subtitle">Spin up a live timer to capture production hours and spot where each edit session goes.</p>
          </div>

          <div className="timestamp-display">
            <div className="time-card current-time">
              <div className="card-heading">
                <h3>Current Time</h3>
                <span className="card-label">Live feed</span>
              </div>
              <div className="time-display">{currentTime.toLocaleTimeString()}</div>
            </div>
            <div className="time-card elapsed-time">
              <div className="card-heading">
                <h3>Elapsed Time</h3>
                <span className="card-label">Session</span>
              </div>
              <div className="time-display">{formatTime(elapsedTime)}</div>
            </div>
          </div>

          <div className="timestamp-controls">
            <button
              className={`timestamp-btn ${isTracking ? 'stop' : 'start'}`}
              onClick={handleStartStop}
              type="button"
            >
              {isTracking ? 'Stop tracking' : 'Start tracking'}
            </button>
            <p className="tracking-hint">
              {isTracking
                ? 'Tracking is live. Click to end the session.'
                : 'Start a live timer to capture how long a task takes.'}
            </p>
          </div>
        </section>
      </div>
      <StampPop
        isOpen={isStampPopOpen}
        onClose={() => setIsStampPopOpen(false)}
        data={timestampResult}
      />
    </section>
  );
};

export default Timestamp;
